import React, { useState } from "react";
import { Input } from "../../../components/ui/input";
import { Button } from "../../../components/ui/button";
import {
  ClipboardList,
  FileCheck,
  PenTool,
  AlertCircle,
} from "lucide-react";
import OnboardingFormShell, {
  OnboardingFormStep,
} from "../../../components/onboarding/OnboardingFormShell";
import SignatureDialog, {
  SignatureType,
} from "../../../components/onboarding/SignatureDialog";
import { useOnboardingForm } from "../../../hooks/useOnboardingForm";

interface InductionChecklistData {
  // Induction Checklist
  checklist: Record<string, boolean>;
  // Sign-off & Signature
  fullName: string;
  signatureDate: string;
  signatureType: string;
  signatureData: string;
  agreed: boolean;
}

interface ChecklistItem {
  id: string;
  label: string;
}

interface ChecklistSection {
  id: string;
  title: string;
  items: ChecklistItem[];
}

const STEPS: OnboardingFormStep[] = [
  { id: 1, label: "Induction Checklist", icon: ClipboardList },
  { id: 2, label: "Sign-off & Signature", icon: PenTool },
];

const CHECKLIST_SECTIONS: ChecklistSection[] = [
  {
    id: "welcome",
    title: "Welcome & Company Overview",
    items: [
      {
        id: "welcome-overview",
        label: "Company history, values and structure overview",
      },
      {
        id: "welcome-manager",
        label: "Introduced to my manager and team",
      },
      {
        id: "welcome-role",
        label: "Overview of my role, responsibilities and expectations",
      },
      {
        id: "welcome-contacts",
        label: "Key contacts — HR, payroll, IT support",
      },
    ],
  },
  {
    id: "whs",
    title: "Workplace Health & Safety (NSW)",
    items: [
      {
        id: "whs-duties",
        label:
          "My WHS duties under the Work Health and Safety Act 2011 (NSW) explained",
      },
      {
        id: "whs-emergency",
        label: "Emergency exits, evacuation procedure and assembly point shown",
      },
      {
        id: "whs-firstaid",
        label: "First aid officers and first aid kit locations",
      },
      {
        id: "whs-incident",
        label: "How to report hazards, incidents and near misses",
      },
      {
        id: "whs-ppe",
        label: "PPE requirements for my role and how to obtain it",
      },
    ],
  },
  {
    id: "policies",
    title: "Policies & Code of Conduct",
    items: [
      { id: "policy-conduct", label: "Code of Conduct" },
      {
        id: "policy-bullying",
        label:
          "Anti-bullying, harassment and discrimination policy (Respect@Work)",
      },
      { id: "policy-leave", label: "Leave and attendance procedures" },
      {
        id: "policy-privacy",
        label: "Privacy and confidentiality obligations",
      },
    ],
  },
  {
    id: "it",
    title: "IT & Systems",
    items: [
      { id: "it-login", label: "Computer and login credentials received" },
      {
        id: "it-email",
        label: "Email and Microsoft Teams account set up",
      },
      {
        id: "it-acceptable-use",
        label: "IT acceptable use and cyber security policy",
      },
      {
        id: "it-passwords",
        label: "Password and data security requirements",
      },
    ],
  },
  {
    id: "site",
    title: "Facilities & Site",
    items: [
      {
        id: "site-access",
        label: "Building access pass / swipe card issued",
      },
      {
        id: "site-amenities",
        label: "Kitchen, bathrooms and amenities shown",
      },
      {
        id: "site-transport",
        label: "Parking and public transport options explained",
      },
    ],
  },
];

const TOTAL_ITEMS = CHECKLIST_SECTIONS.reduce(
  (sum, section) => sum + section.items.length,
  0
);

const initialData: InductionChecklistData = {
  checklist: {},
  fullName: "",
  signatureDate: new Date().toISOString().split("T")[0],
  signatureType: "Draw",
  signatureData: "",
  agreed: false,
};

interface InductionChecklistFormProps {
  /** HR preview mode: renders the hire's submitted form read-only */
  previewRequestId?: string | null;
}

const InductionChecklistForm: React.FC<InductionChecklistFormProps> = ({
  previewRequestId,
}) => {
  const {
    formData,
    updateField,
    currentStep,
    setCurrentStep,
    loading,
    saving,
    isCompleted,
    isEditing,
    submittedAt,
    startEditing,
    saveProgress,
    submit,
    isPreview,
  } = useOnboardingForm<InductionChecklistData>(
    "induction-checklist",
    initialData,
    { previewRequestId }
  );

  const [isSignatureDialogOpen, setIsSignatureDialogOpen] = useState(false);

  const readOnly = isPreview || (isCompleted && !isEditing);

  const totalItems = TOTAL_ITEMS;
  const checkedCount = CHECKLIST_SECTIONS.reduce(
    (sum, section) =>
      sum +
      section.items.filter((item) => formData.checklist[item.id]).length,
    0
  );
  const allChecked = checkedCount === totalItems;
  const remaining = totalItems - checkedCount;

  const canSubmit =
    !!formData.fullName.trim() &&
    !!formData.signatureData &&
    formData.agreed &&
    allChecked;

  const inputClass = "h-11 rounded-xl border-gray-200 bg-gray-50";
  const labelClass = "text-sm font-medium text-gray-900";

  const toggleItem = (itemId: string, checked: boolean) => {
    updateField("checklist", { ...formData.checklist, [itemId]: checked });
  };

  // ─── STEP 1: Induction Checklist ───
  const renderChecklist = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">
          Company Induction Checklist
        </h3>
        <p className="text-sm text-gray-500">
          Tick each item as it is covered during your site and company
          induction. All items must be completed before you can sign off.
        </p>
      </div>

      {/* Overall progress */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-gray-900">
            {checkedCount} of {totalItems} items completed
          </span>
          <span className="text-sm text-gray-500">
            {Math.round((checkedCount / totalItems) * 100)}%
          </span>
        </div>
        <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all ${
              allChecked ? "bg-green-500" : "bg-slate-900"
            }`}
            style={{ width: `${(checkedCount / totalItems) * 100}%` }}
          />
        </div>
      </div>

      {CHECKLIST_SECTIONS.map((section) => {
        const sectionChecked = section.items.filter(
          (item) => formData.checklist[item.id]
        ).length;

        return (
          <div
            key={section.id}
            className="bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-3"
          >
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold text-gray-900">
                {section.title}
              </h4>
              <span className="text-xs text-gray-500">
                {sectionChecked}/{section.items.length}
              </span>
            </div>
            <div className="space-y-2">
              {section.items.map((item) => (
                <label
                  key={item.id}
                  className="flex items-start gap-2 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={!!formData.checklist[item.id]}
                    onChange={(e) => toggleItem(item.id, e.target.checked)}
                    disabled={readOnly}
                    className="w-4 h-4 rounded border-gray-300 mt-0.5"
                  />
                  <span className="text-sm text-gray-700">{item.label}</span>
                </label>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );

  // ─── STEP 2: Sign-off & Signature ───
  const renderSignOff = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">
          Sign-off & Signature
        </h3>
        <p className="text-sm text-gray-500">
          Read the acknowledgement carefully before signing
        </p>
      </div>

      {!allChecked && (
        <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl p-4">
          <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-amber-700">
            Please complete all {totalItems} checklist items before signing. You
            still have {remaining} item(s) to tick off.
          </p>
        </div>
      )}

      <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
        <div className="flex items-start gap-2">
          <FileCheck className="w-5 h-5 text-gray-500 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-gray-700 space-y-2">
            <p className="font-medium text-gray-900">Acknowledgement</p>
            <p>
              I confirm that I have completed each of the induction items ticked
              in this checklist, that I have had the opportunity to ask
              questions, and that I understand I can raise any further questions
              with my manager or HR at any time.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <label className={labelClass}>
            Full Legal Name for Signature <span className="text-red-500">*</span>
          </label>
          <Input
            placeholder="Your full legal name"
            value={formData.fullName}
            onChange={(e) => updateField("fullName", e.target.value)}
            disabled={readOnly}
            className={inputClass}
          />
        </div>
        <div className="space-y-2">
          <label className={labelClass}>Date</label>
          <Input
            type="date"
            value={formData.signatureDate}
            onChange={(e) => updateField("signatureDate", e.target.value)}
            disabled={readOnly}
            className={inputClass}
          />
        </div>
      </div>

      {/* Digital Signature */}
      <div className="space-y-2">
        <label className={labelClass}>Digital Signature</label>
        {formData.signatureData ? (
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-200">
            <div className="flex items-center gap-3">
              {formData.signatureType === "Draw" ? (
                <img
                  src={formData.signatureData}
                  alt="Signature"
                  className="max-h-10"
                />
              ) : (
                <span className="font-signature text-2xl">
                  {formData.signatureData}
                </span>
              )}
            </div>
            {!readOnly && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => updateField("signatureData", "")}
                className="rounded-lg"
              >
                Clear
              </Button>
            )}
          </div>
        ) : (
          <Button
            type="button"
            variant="outline"
            onClick={() => setIsSignatureDialogOpen(true)}
            disabled={readOnly}
            className="w-full rounded-xl border-dashed border-gray-300 py-6 gap-2"
          >
            <PenTool className="w-4 h-4" />
            Click to Sign
          </Button>
        )}
      </div>

      <label className="flex items-start gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={formData.agreed}
          onChange={(e) => updateField("agreed", e.target.checked)}
          disabled={readOnly}
          className="w-4 h-4 rounded border-gray-300 mt-0.5"
        />
        <span className="text-sm text-gray-700">
          I agree to the acknowledgement above and confirm this electronic
          signature is legally binding.
        </span>
      </label>
    </div>
  );

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return renderChecklist();
      case 2:
        return renderSignOff();
      default:
        return renderChecklist();
    }
  };

  return (
    <>
      <OnboardingFormShell
        title="Company Induction Checklist"
        steps={STEPS}
        currentStep={currentStep}
        onStepChange={setCurrentStep}
        onSaveProgress={saveProgress}
        onSubmit={submit}
        saving={saving}
        loading={loading}
        submitDisabled={!canSubmit}
        readOnly={readOnly}
        submittedAt={submittedAt}
        onEdit={isPreview ? undefined : startEditing}
        backTo={previewRequestId ? `/my-onboarding?requestId=${previewRequestId}` : undefined}
        isResubmission={isCompleted}
      >
        {renderStepContent()}
      </OnboardingFormShell>

      <SignatureDialog
        open={isSignatureDialogOpen}
        onOpenChange={setIsSignatureDialogOpen}
        onConfirm={(type: SignatureType, data: string) => {
          updateField("signatureType", type);
          updateField("signatureData", data);
        }}
      />
    </>
  );
};

export default InductionChecklistForm;
